import {
  FinchConnectionDetailFragment,
  useAnalytics,
  useCreateFinchSessionMutation,
  useResponseNotification,
} from '@alice-financial/alice-sdk'
import { Alert, Box, Button, Grid, TextField, Typography } from '@alice-financial/pretext-ui'
import AddIcon from '@mui/icons-material/Add'
import LockIcon from '@mui/icons-material/Lock'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import * as React from 'react'
import { useRef, useState } from 'react'
import { FinchConnectResultAlert } from '../../externalServices/finch/FinchConnectResultAlert'
import { NeedingAuthFinchConnections } from './NeedingAuthFinchConnections'
import { validateConnectionDisplayLabel } from './utils'

type FinchConnectedProps = {
  organizationId: number
  onFinchSessionCreated: (finchSessionInfo: { finchSessionId: string; finchConnectionId: number }) => void
  isFinchIframeOpened: boolean
  doneFinchConnections: FinchConnectionDetailFragment[]
  needingAuthFinchConnections: FinchConnectionDetailFragment[]
  allFinchConnections: FinchConnectionDetailFragment[]
}

export const FinchConnected = ({
  organizationId,
  onFinchSessionCreated,
  isFinchIframeOpened,
  doneFinchConnections,
  needingAuthFinchConnections,
  allFinchConnections,
}: FinchConnectedProps) => {
  const analytics = useAnalytics()
  const { notifyError } = useResponseNotification()
  const [showAddConnectionForm, setShowAddConnectionForm] = useState(false)
  const [connectionDisplayLabel, setConnectionDisplayLabel] = useState('')
  const connectionDisplayLabelInputRef = useRef<HTMLInputElement>(null)

  // Tracks which previously created (but unauthenticated) Finch connection is currently
  // being authenticated via the iframe, allowing its CTA button to show a disabled loading state.
  const [inProgressFinchConnectionId, setInProgressFinchConnectionId] = useState<number | null>(null)

  const { mutate: createFinchSession, isLoading: isCreatingSession } = useCreateFinchSessionMutation({
    onSuccess: (data) => {
      const finchConnection = data.createFinchSession?.finchConnection
      const finchSessionId = finchConnection?.sessionId
      const finchConnectionId = finchConnection?.id
      if (finchSessionId && finchConnectionId) {
        onFinchSessionCreated({ finchSessionId, finchConnectionId })
        // Not calling `setInProgressFinchConnectionId(null)` here - authentication is starting
        // in the iframe (which is initialized with this `finchSessionId`)
      }
    },
    onError: (error) => {
      console.error('Failed to create Finch session:', error)
      notifyError(
        'Unable to connect to payroll provider. Please try again or contact support if the issue persists.'
      )
      setInProgressFinchConnectionId(null)
    },
  })

  const handleConnectAnother = () => {
    analytics.track('finch_connect.connect_another.clicked')
    setShowAddConnectionForm(true)
    setConnectionDisplayLabel('')
    setTimeout(() => connectionDisplayLabelInputRef.current?.focus(), 0) // move focus to the display label input
  }

  const handleSaveAndConnect = () => {
    analytics.track('finch_connect.connect_payroll_account.clicked')
    createFinchSession({
      input: {
        organizationId,
        displayLabel: connectionDisplayLabel.trim(),
      },
    })
  }

  const handleResumeConnection = (finchConnectionId: number) => {
    analytics.track('finch_connect.resume_connection.clicked')
    setInProgressFinchConnectionId(finchConnectionId)

    createFinchSession({
      input: {
        organizationId,
        finchConnectionId,
      },
    })
  }

  const handleCancelAddConnection = () => {
    setShowAddConnectionForm(false)
    setConnectionDisplayLabel('')
  }

  const validationResult = validateConnectionDisplayLabel(connectionDisplayLabel, allFinchConnections)
  const isLabelInvalid = !validationResult.isValid

  return (
    <>
      <FinchConnectResultAlert />
      <Typography variant="h3" color="text.secondary" gutterBottom>
        Grant payroll view permission
      </Typography>
      <Typography variant="body2" gutterBottom>
        <strong>Successfully connected</strong>
      </Typography>
      <Alert icon={<LockIcon />} severity="info">
        Connections are encrypted. Alice works with Finch, a leading provider of HRIS integrations, for
        read-only access.
      </Alert>

      {doneFinchConnections.length > 0 && (
        <>
          <Typography variant="body2" gutterBottom>
            Read access has been granted to the following payroll accounts:
          </Typography>
          <Typography variant="body2" component="ul" gutterBottom>
            {doneFinchConnections.map((conn) => (
              <li key={conn.id}>
                <strong>{conn.displayLabel || 'Primary account'}</strong>
              </li>
            ))}
          </Typography>
        </>
      )}

      {/* Display connections that need authentication */}
      <NeedingAuthFinchConnections
        needingAuthFinchConnections={needingAuthFinchConnections}
        inProgressFinchConnectionId={inProgressFinchConnectionId}
        onResumeConnection={handleResumeConnection}
      />

      {!showAddConnectionForm && (
        <Button
          startIcon={<AddIcon />}
          onClick={handleConnectAnother}
          variant="outlined"
          sx={{ mb: 3 }}
          disabled={isFinchIframeOpened}
        >
          Connect another payroll account
        </Button>
      )}

      {showAddConnectionForm && (
        <Box mt={2} mb={3}>
          <TextField
            inputRef={connectionDisplayLabelInputRef}
            label="What should we call our account in your payroll system?"
            value={connectionDisplayLabel}
            onChange={(e) => setConnectionDisplayLabel(e.target.value)}
            fullWidth
            required
            error={isLabelInvalid}
            helperText={validationResult.errorMessage || ''}
            placeholder="e.g., Payroll for Main St location"
          />
          <Grid gap={1} mt={1}>
            <Button variant="text" color="primary" sx={{ mt: 1 }} onClick={handleCancelAddConnection}>
              Cancel
            </Button>
            <Button
              endIcon={<OpenInNewIcon />}
              onClick={handleSaveAndConnect}
              variant="contained"
              color="primary"
              sx={{ mt: 1 }}
              disabled={!connectionDisplayLabel || isLabelInvalid || isCreatingSession || isFinchIframeOpened}
            >
              {isCreatingSession || isFinchIframeOpened ? 'Connecting...' : 'Connect payroll account'}
            </Button>
          </Grid>
        </Box>
      )}
    </>
  )
}
